import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../utils/supabase';
import type { ComparisonStats } from '../types';

interface UseComparisonStatsReturn {
  comparisonStats: ComparisonStats | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useComparisonStats(
  bowlerId: number | null,
  playerAverage: number,
  seasonYear?: number
): UseComparisonStatsReturn {
  const [comparisonStats, setComparisonStats] = useState<ComparisonStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchComparisonStats = useCallback(async () => {
    if (!bowlerId || playerAverage <= 0) {
      setComparisonStats(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get all players' weekly data to calculate proper averages and rankings
      const { data: allPlayersData, error: playersError } = await supabase
        .from('league_bowler_week_stats')
        .select('bowler_id, week_num, total_pins, total_games')
        .not('total_pins', 'is', null)
        .not('total_games', 'is', null)
        .gt('total_games', 0);

      if (playersError) {
        throw new Error(`Failed to fetch players data: ${playersError.message}`);
      }

      // Calculate each player's most recent week average
      const playerAverages = new Map<number, { average: number; weekNumber: number }>();
      
      if (allPlayersData) {
        // Group by player and find their most recent week
        const playerWeeks = new Map<number, Array<{ week_num: number; total_pins: number; total_games: number }>>();
        
        allPlayersData.forEach((row) => {
          if (!playerWeeks.has(row.bowler_id)) {
            playerWeeks.set(row.bowler_id, []);
          }
          playerWeeks.get(row.bowler_id)!.push({
            week_num: row.week_num,
            total_pins: row.total_pins,
            total_games: row.total_games,
          });
        });

        // Calculate most recent week average for each player
        playerWeeks.forEach((weeks, playerId) => {
          const mostRecentWeek = weeks.reduce((latest, current) => 
            current.week_num > latest.week_num ? current : latest
          );
          
          const average = mostRecentWeek.total_pins / mostRecentWeek.total_games;
          playerAverages.set(playerId, {
            average,
            weekNumber: mostRecentWeek.week_num,
          });
        });
      }

      // Calculate league average excluding this player
      const otherPlayersAverages = Array.from(playerAverages.entries())
        .filter(([playerId]) => playerId !== bowlerId)
        .map(([, data]) => data.average);

      const leagueAverage = otherPlayersAverages.length > 0 
        ? otherPlayersAverages.reduce((sum, avg) => sum + avg, 0) / otherPlayersAverages.length 
        : 0;

      // Calculate player rank and total players
      const allAverages = Array.from(playerAverages.entries())
        .map(([playerId, data]) => ({ playerId, average: data.average }))
        .sort((a, b) => b.average - a.average);

      const totalPlayers = allAverages.length;
      const playerIndex = allAverages.findIndex(p => p.playerId === bowlerId);
      const playerRank = playerIndex !== -1 ? playerIndex + 1 : totalPlayers;

      // Calculate percentage difference
      const percentageDifference = leagueAverage > 0 
        ? ((playerAverage - leagueAverage) / leagueAverage) * 100 
        : 0;

      const comparison: ComparisonStats = {
        player_average: Math.round(playerAverage * 100) / 100,
        league_average: Math.round(leagueAverage * 100) / 100,
        percentage_difference: Math.round(percentageDifference * 100) / 100,
        is_above_average: playerAverage > leagueAverage,
        player_rank: playerRank,
        total_players: totalPlayers,
      };

      setComparisonStats(comparison);
    } catch (err) {
      console.error('Error fetching comparison stats:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch comparison statistics'));
    } finally {
      setLoading(false);
    }
  }, [bowlerId, playerAverage, seasonYear]);

  const refetch = async () => {
    await fetchComparisonStats();
  };

  useEffect(() => {
    fetchComparisonStats();
  }, [fetchComparisonStats]);

  return {
    comparisonStats,
    loading,
    error,
    refetch,
  };
}